import { useMemo } from "react";
import { Link } from "react-router-dom";
import { Box, CircularProgress, Divider, Stack, Typography } from "@mui/material";
import { ClaimsSummaryRow, ResourceName } from "@/types";
import { formatUSD } from "@/utils/formatters/currencyFormatter";
import { CompactTable, type CompactTableColumn } from "@/components/common/CompactTable";
import { useResourcePermissions } from "@/hooks/useResourcePermissions";
import { SectionHeader } from "../SectionHeader";
import { HintIcon } from "@/components/ui/mui/HintIcon";
import { ContractLaserDeductibleChip, LaseredDeductibleChip } from "@/components/ui/mui/LaseredDeductibleChip";
import { LaserChipWithReview } from "@/components/common/DeductibleReviewFlag";
import { getColumnLabel, getColumnHint } from "./shared/claimsSummaryColumnsHelper";
import { ClaimantPolicyNoteCell } from "@/components/common/ClaimantPolicyNoteCell";

// ==================== TYPES ====================

interface SummaryTableProps {
  rows: ClaimsSummaryRow[];
  loading: boolean;
  /** Selected policy id — scopes the editable per-claimant Note (US 5273). */
  policyId?: string;
  /**
   * True when the policy has an aggregate specific deductible (#3302). Shows the
   * ASD drawdown columns, which explain why Owed can be $0 for a claimant who IS
   * over their specific deductible. Non-ASD policies render exactly as before.
   */
  showAggSpec?: boolean;
}

// ==================== SUBCOMPONENTS ====================

/** Single metric shown in the detail panel */
const Metric = ({ label, value, color }: { label: string; value: number; color?: string }) => (
  <Box sx={{ minWidth: 80 }}>
    <Typography variant="caption" color="text.disabled" sx={{ lineHeight: 1.2 }}>
      {label}
    </Typography>
    <Typography
      variant="body2"
      sx={{ fontWeight: 500, fontVariantNumeric: "tabular-nums", color: color || "text.primary" }}
    >
      {formatUSD(value)}
    </Typography>
  </Box>
);

/**
 * Renders an ASD amount, distinguishing "$0 — nothing payable" from "not calculated".
 * A dash is deliberate: showing $0 for an absent allocation would read as a real
 * zero and defeat the point of the column.
 */
const AsdAmount = ({ value }: { value?: number }) => (
  <Typography
    variant="body2"
    sx={{ fontVariantNumeric: "tabular-nums" }}
    color={value === undefined ? "text.secondary" : undefined}
  >
    {value === undefined ? "—" : formatUSD(value)}
  </Typography>
);

/** Inline detail panel shown when a row is expanded */
const RowDetailPanel = ({ row }: { row: ClaimsSummaryRow }) => (
  <Stack direction={{ xs: "column", sm: "row" }} spacing={3} divider={<Divider orientation="vertical" flexItem />}>
    <Box>
      <Typography variant="overline" sx={{ fontSize: "0.6rem", color: "text.disabled", letterSpacing: "0.1em" }}>
        Spend
      </Typography>
      <Stack direction="row" spacing={2.5} sx={{ mt: 0.5 }}>
        <Metric label={getColumnLabel("medical")} value={row.totalMedical} />
        <Metric label={getColumnLabel("rx")} value={row.totalRX} />
      </Stack>
    </Box>
    <Box>
      <Typography variant="overline" sx={{ fontSize: "0.6rem", color: "text.disabled", letterSpacing: "0.1em" }}>
        Reimbursement
      </Typography>
      <Stack direction="row" spacing={2.5} sx={{ mt: 0.5 }}>
        <Metric label={getColumnLabel("requested")} value={row.totalRequested} />
        <Metric
          label={getColumnLabel("reimbursed")}
          value={row.totalReimbursed}
          color={row.totalReimbursed > 0 ? "success.main" : undefined}
        />
        <Metric
          label={getColumnLabel("pended")}
          value={row.totalPended}
          color={row.totalPended > 0 ? "warning.main" : undefined}
        />
        <Metric
          label={getColumnLabel("denied")}
          value={row.totalDenied}
          color={row.totalDenied > 0 ? "error.main" : undefined}
        />
      </Stack>
    </Box>
  </Stack>
);

// ==================== HELPERS ====================

/** Column header with optional hint icon */
const HeaderWithHint = ({ columnKey }: { columnKey: string }) => {
  const hint = getColumnHint(columnKey);
  return (
    <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
      {getColumnLabel(columnKey)}
      {hint && <HintIcon hint={hint} variant="info" />}
    </Box>
  );
};

// ==================== COLUMNS ====================

const baseColumns: CompactTableColumn<ClaimsSummaryRow>[] = [
  {
    key: "claimant",
    label: getColumnLabel("claimant"),
    width: "1.5fr",
    render: (row) => (
      // The name shrinks and wraps; the flag does not (it overprinted the next
      // column's amount at laptop widths, UX review #1).
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
        <Link
          to={`/claimants/${row.id}/show`}
          onClick={(e) => e.stopPropagation()}
          style={{ textDecoration: "none", minWidth: 0 }}
        >
          <Typography
            variant="body2"
            sx={{
              color: "primary.main",
              textDecoration: "underline",
              fontWeight: 500,
              overflowWrap: "anywhere",
              "&:hover": { color: "primary.dark" },
            }}
          >
            {row.name}
          </Typography>
        </Link>
        {/* A contract laser (#5287) and a straight laser both give the claimant an
            individual deductible, so both are indicated -- but with the distinct chip
            each already has. Before #6929 a contract-lasered claimant got NEITHER,
            because the row flag was read straight off `is_lasered`. */}
        <LaserChipWithReview
          pending={row.isDeductibleReviewPending}
          claimantId={row.id}
          policyId={row.policyId}
          claimantName={row.name}
        >
          {row.isContractLasered ? <ContractLaserDeductibleChip /> : row.isLasered && <LaseredDeductibleChip />}
        </LaserChipWithReview>
      </Box>
    ),
  },
  {
    key: "memberId",
    label: getColumnLabel("memberId"),
    width: "1fr",
    render: (row) => (
      <Typography variant="body2" color="text.secondary">
        {row.memberId || "—"}
      </Typography>
    ),
  },
  {
    key: "totalYTD",
    label: <HeaderWithHint columnKey="totalYTD" />,
    width: "120px",
    align: "right",
    render: (row) => (
      <Typography variant="body2" sx={{ fontVariantNumeric: "tabular-nums" }}>
        {formatUSD(row.totalYTD)}
      </Typography>
    ),
  },
  {
    key: "deductible",
    label: <HeaderWithHint columnKey="deductible" />,
    width: "120px",
    align: "right",
    render: (row) => (
      <Typography variant="body2" sx={{ fontVariantNumeric: "tabular-nums" }}>
        {formatUSD(row.specDeductible)}
      </Typography>
    ),
  },
  {
    key: "owed",
    label: <HeaderWithHint columnKey="owed" />,
    width: "120px",
    align: "right",
    render: (row) => (
      <Typography variant="body2" sx={{ fontVariantNumeric: "tabular-nums" }}>
        {formatUSD(row.totalOwed)}
      </Typography>
    ),
  },
  {
    key: "variance",
    label: <HeaderWithHint columnKey="variance" />,
    width: "120px",
    align: "right",
    render: (row) => (
      <Typography
        variant="body2"
        sx={{
          fontVariantNumeric: "tabular-nums",
          fontWeight: 600,
          color: row.variance > 0 ? "error.main" : row.variance < 0 ? "success.main" : "text.primary",
        }}
      >
        {formatUSD(row.variance)}
      </Typography>
    ),
  },
];

/**
 * ASD drawdown columns (#3302), spliced in after Deductible only on aggregate-
 * specific-deductible policies.
 *
 * Values are optional: the engine emits them with `omitempty`, so a claimant with no
 * allocation yet renders a dash rather than a misleading $0. `formatUSD(0)` is a
 * meaningful "nothing payable"; undefined means "not calculated".
 */
const aggSpecColumns: CompactTableColumn<ClaimsSummaryRow>[] = [
  {
    key: "specificExcess",
    label: <HeaderWithHint columnKey="specificExcess" />,
    width: "130px",
    align: "right",
    render: (row) => <AsdAmount value={row.specificExcess} />,
  },
  {
    key: "asdConsumed",
    label: <HeaderWithHint columnKey="asdConsumed" />,
    width: "150px",
    align: "right",
    render: (row) => <AsdAmount value={row.asdConsumed} />,
  },
  {
    key: "carrierPayable",
    label: <HeaderWithHint columnKey="carrierPayable" />,
    width: "140px",
    align: "right",
    render: (row) => <AsdAmount value={row.carrierPayable} />,
  },
];

// ==================== MAIN COMPONENT ====================

export const SummaryTable = ({ rows, loading, policyId, showAggSpec }: SummaryTableProps) => {
  const { canEdit } = useResourcePermissions(ResourceName.CLAIMANTS);

  // Append the editable Note column (US 5273 / ADR-052 §10) to the static base.
  const columns = useMemo<CompactTableColumn<ClaimsSummaryRow>[]>(() => {
    const cols = [...baseColumns];

    // Insert the ASD drawdown right after Deductible so the reader goes
    // deductible -> excess -> applied to corridor -> payable -> owed.
    if (showAggSpec) {
      const afterDeductible = cols.findIndex((c) => c.key === "deductible") + 1;
      cols.splice(afterDeductible, 0, ...aggSpecColumns);
    }

    return [
      ...cols,
      {
        key: "note",
        label: "Notes",
        width: "1.4fr",
        render: (row) => (
          <ClaimantPolicyNoteCell claimantId={row.id} policyId={policyId} note={row.claimantNote} canEdit={canEdit} />
        ),
      },
    ];
  }, [policyId, canEdit, showAggSpec]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <SectionHeader title="Summary" subtitle="Expand a row for spend and reimbursement details" count={rows.length}>
      <CompactTable
        data={rows}
        columns={columns}
        expandRow={(row) => <RowDetailPanel row={row} />}
        rowsPerPage={10}
        emptyMessage="No claimants have reached their specific deductible for the selected policy."
      />
    </SectionHeader>
  );
};
